(function initializeStoreOpeningHours(root, factory) {
  const api = factory();

  if (typeof module === 'object' && module.exports) {
    module.exports = api;
  }

  if (root) {
    root.StoreOpeningHours = api;
  }
})(typeof globalThis !== 'undefined' ? globalThis : this, () => {
  const TIME_ZONE = 'Europe/London';
  const DAY_KEYS = [
    'monday',
    'tuesday',
    'wednesday',
    'thursday',
    'friday',
    'saturday',
    'sunday',
  ];
  const DAY_LABELS = DAY_KEYS.map((day) => `${day.charAt(0).toUpperCase()}${day.slice(1)}`);
  const TIME_PATTERN = /^(?:[01]\d|2[0-3]):[0-5]\d$/;

  function parseTime(value) {
    const time = typeof value === 'string' ? value.trim() : '';
    if (!TIME_PATTERN.test(time)) return null;

    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  }

  function formatTime(minutes) {
    if (!Number.isInteger(minutes) || minutes < 0 || minutes >= 24 * 60) return '';

    const hours = Math.floor(minutes / 60);
    const displayHours = hours % 12 || 12;
    const displayMinutes = String(minutes % 60).padStart(2, '0');
    return `${displayHours}:${displayMinutes}${hours < 12 ? 'am' : 'pm'}`;
  }

  function parseSchedule(value) {
    let source = value;

    if (typeof value === 'string') {
      try {
        source = JSON.parse(value);
      } catch (_error) {
        return null;
      }
    }

    if (!source || typeof source !== 'object' || Array.isArray(source)) return null;
    if (source.timeZone && source.timeZone !== TIME_ZONE) return null;
    if (!source.days || typeof source.days !== 'object' || Array.isArray(source.days)) {
      return null;
    }

    const days = [];

    for (let index = 0; index < DAY_KEYS.length; index += 1) {
      const key = DAY_KEYS[index];
      const sourceDay = source.days[key];

      if (!sourceDay || typeof sourceDay !== 'object' || Array.isArray(sourceDay)) return null;

      const closed = sourceDay.closed === true;
      const open = typeof sourceDay.open === 'string' ? sourceDay.open.trim() : '';
      const close = typeof sourceDay.close === 'string' ? sourceDay.close.trim() : '';

      if (closed) {
        if (open || close) return null;

        days.push({
          key,
          label: DAY_LABELS[index],
          closed: true,
          open: '',
          close: '',
          openMinutes: null,
          closeMinutes: null,
        });
        continue;
      }

      const openMinutes = parseTime(open);
      const closeMinutes = parseTime(close);

      if (openMinutes === null || closeMinutes === null || closeMinutes <= openMinutes) {
        return null;
      }

      days.push({
        key,
        label: DAY_LABELS[index],
        closed: false,
        open,
        close,
        openMinutes,
        closeMinutes,
      });
    }

    return {
      timeZone: TIME_ZONE,
      days,
    };
  }

  function getLondonDateTime(date = new Date()) {
    const currentDate = date instanceof Date ? date : new Date(date);
    if (Number.isNaN(currentDate.getTime())) return null;

    try {
      const parts = new Intl.DateTimeFormat('en-GB', {
        timeZone: TIME_ZONE,
        weekday: 'long',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
      }).formatToParts(currentDate);
      const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
      const dayIndex = DAY_KEYS.indexOf(String(values.weekday || '').toLowerCase());
      const hours = Number.parseInt(values.hour, 10);
      const minutes = Number.parseInt(values.minute, 10);

      if (
        dayIndex === -1 ||
        !Number.isInteger(hours) ||
        !Number.isInteger(minutes) ||
        hours < 0 ||
        hours > 23 ||
        minutes < 0 ||
        minutes > 59
      ) {
        return null;
      }

      return {
        dayIndex,
        minutes: hours * 60 + minutes,
      };
    } catch (_error) {
      return null;
    }
  }

  function findNextOpening(schedule, currentDayIndex, currentMinutes) {
    for (let offset = 0; offset <= DAY_KEYS.length; offset += 1) {
      const dayIndex = (currentDayIndex + offset) % DAY_KEYS.length;
      const day = schedule.days[dayIndex];

      if (day.closed) continue;
      if (offset === 0 && currentMinutes >= day.openMinutes) continue;

      return {
        dayIndex,
        day,
        isToday: offset === 0,
      };
    }

    return null;
  }

  function unavailableStatus() {
    return {
      state: 'unavailable',
      label: 'Hours unavailable',
      transition: '',
      currentDayIndex: null,
    };
  }

  function getStatus(value, date = new Date()) {
    const schedule =
      value &&
      value.timeZone === TIME_ZONE &&
      Array.isArray(value.days) &&
      value.days.length === DAY_KEYS.length
        ? value
        : parseSchedule(value);
    const current = getLondonDateTime(date);

    if (!schedule || !current) return unavailableStatus();

    const today = schedule.days[current.dayIndex];
    const isOpen =
      !today.closed &&
      current.minutes >= today.openMinutes &&
      current.minutes < today.closeMinutes;

    if (isOpen) {
      return {
        state: 'open',
        label: 'Open',
        transition: `Closes ${formatTime(today.closeMinutes)}`,
        currentDayIndex: current.dayIndex,
      };
    }

    const nextOpening = findNextOpening(schedule, current.dayIndex, current.minutes);

    return {
      state: 'closed',
      label: 'Closed',
      transition: nextOpening
        ? `Opens ${nextOpening.isToday ? '' : `${nextOpening.day.label} `}${formatTime(
            nextOpening.day.openMinutes
          )}`
        : '',
      currentDayIndex: current.dayIndex,
    };
  }

  return {
    TIME_ZONE,
    DAY_KEYS: [...DAY_KEYS],
    DAY_LABELS: [...DAY_LABELS],
    parseTime,
    formatTime,
    parseSchedule,
    getLondonDateTime,
    getStatus,
  };
});
